/**
 * EdgeService — hexastore CRUD for the graph edges table.
 *
 * All entity relationships flow through this service. Compound indexes
 * on [subject+predicate], [object+predicate], etc. give O(1) lookups
 * for common query patterns.
 */

import { db } from '../database/schema.js'
import type { SyncEvent } from '../database/schema.js'
import type { Edge } from '../schemas/edge.js'
import type { EdgeCreate, Predicate, NodeRef } from '../schemas/edge.js'
import { isApplyingServerChanges } from '../storage/server-apply.js'
import { schedulePush } from '../storage/sync-push-scheduler.js'

/** Build a node reference string: "type:uuid" */
export function nodeRef(type: string, id: string): NodeRef {
  return `${type}:${id}`
}

/** Parse a node reference string back to { type, id } */
export function parseNodeRef(ref: NodeRef): { type: string; id: string } {
  const colonIdx = ref.indexOf(':')
  if (colonIdx === -1) throw new Error(`Invalid node ref: ${ref}`)
  return { type: ref.slice(0, colonIdx), id: ref.slice(colonIdx + 1) }
}

class EdgeService {
  private persistSyncEvent(
    edgeId: string,
    operation: 'create' | 'delete',
    data?: Record<string, unknown>
  ): void {
    if (isApplyingServerChanges()) return
    const event: SyncEvent = {
      id: crypto.randomUUID(),
      entityType: 'edge',
      entityId: edgeId,
      operation,
      data: data ?? {},
      timestamp: new Date().toISOString(),
      conflictResolved: false,
    }
    db.syncEvents.add(event).catch(() => {})
    schedulePush()
  }

  /** Create an edge and return it */
  async create(data: EdgeCreate): Promise<Edge> {
    const edge: Edge = {
      id: crypto.randomUUID(),
      subject: data.subject,
      predicate: data.predicate,
      object: data.object,
      properties: data.properties,
      version: 0,
      createdAt: new Date().toISOString(),
    }
    await db.edges.add(edge)
    this.persistSyncEvent(edge.id, 'create', {
      subject: edge.subject,
      predicate: edge.predicate,
      object: edge.object,
      properties: edge.properties,
    })
    return edge
  }

  private persistEdgeDelete(edge: Edge): void {
    this.persistSyncEvent(edge.id, 'delete', {
      subject: edge.subject,
      predicate: edge.predicate,
      object: edge.object,
    })
  }

  /** Delete an edge by ID */
  async delete(id: string): Promise<void> {
    const edge = await db.edges.get(id)
    await db.edges.delete(id)
    if (edge) {
      this.persistEdgeDelete(edge)
    }
  }

  /** Delete all edges matching subject + predicate + object exactly */
  async deleteExact(subject: NodeRef, predicate: Predicate, object: NodeRef): Promise<number> {
    const matches = await db.edges
      .where('[subject+predicate]')
      .equals([subject, predicate])
      .filter(e => e.object === object)
      .toArray()
    const ids = matches.map(e => e.id)
    await db.edges.bulkDelete(ids)
    for (const edge of matches) {
      this.persistEdgeDelete(edge)
    }
    return ids.length
  }

  /** Delete all edges where this node is subject OR object */
  async deleteAllForNode(ref: NodeRef): Promise<number> {
    const [asSubject, asObject] = await Promise.all([
      db.edges.where('subject').equals(ref).toArray(),
      db.edges.where('object').equals(ref).toArray(),
    ])
    const allEdges = [...asSubject, ...asObject]
    const ids = allEdges.map(e => e.id)
    await db.edges.bulkDelete(ids)
    for (const edge of allEdges) {
      this.persistEdgeDelete(edge)
    }
    return ids.length
  }

  // --- Query by subject ---

  /** "All features of plan:123" → query [subject+predicate] */
  async getBySubject(subject: NodeRef, predicate: Predicate): Promise<Edge[]> {
    return db.edges.where('[subject+predicate]').equals([subject, predicate]).toArray()
  }

  /** Get all edges from a subject (any predicate) */
  async getAllFromSubject(subject: NodeRef): Promise<Edge[]> {
    return db.edges.where('subject').equals(subject).toArray()
  }

  // --- Query by object ---

  /** "All plans that use route:101" → query [object+predicate] */
  async getByObject(object: NodeRef, predicate: Predicate): Promise<Edge[]> {
    return db.edges.where('[object+predicate]').equals([object, predicate]).toArray()
  }

  /** Get all edges pointing to an object (any predicate) */
  async getAllToObject(object: NodeRef): Promise<Edge[]> {
    return db.edges.where('object').equals(object).toArray()
  }

  // --- Query by predicate ---

  /** "All has-feature edges" → useful for bulk operations */
  async getByPredicate(predicate: Predicate): Promise<Edge[]> {
    return db.edges.where('predicate').equals(predicate).toArray()
  }

  // --- Convenience: resolve object IDs ---

  /** Get the object node IDs for a subject+predicate query */
  async getObjectIds(subject: NodeRef, predicate: Predicate): Promise<string[]> {
    const edges = await this.getBySubject(subject, predicate)
    return edges.map(e => parseNodeRef(e.object).id)
  }

  /** Get the subject node IDs for an object+predicate query */
  async getSubjectIds(object: NodeRef, predicate: Predicate): Promise<string[]> {
    const edges = await this.getByObject(object, predicate)
    return edges.map(e => parseNodeRef(e.subject).id)
  }

  // --- Check existence ---

  /** Does an edge exist between subject and object with this predicate? */
  async exists(subject: NodeRef, predicate: Predicate, object: NodeRef): Promise<boolean> {
    const count = await db.edges
      .where('[subject+predicate]')
      .equals([subject, predicate])
      .filter(e => e.object === object)
      .count()
    return count > 0
  }

  /** Update edge properties */
  async updateProperties(id: string, properties: Record<string, unknown>): Promise<void> {
    await db.edges.update(id, { properties })
  }
}

export const edgeService = new EdgeService()
